import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AnalyticsContext } from '@atlaskit/analytics-next';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { getHamsKey } from '../../../services/product-to-hams-key';
import { useMarkDialogAsSeen } from '../../../services/use-mark-dialog-as-seen';
import { ModalEntryPoint } from '../shared/modal-entry-point';

import { DIALOG_CONFIG } from './dialog-config';
import { entryPoint, type EntryPointParams } from './entrypoint';

export function FeatureOnboardingModal(): React.JSX.Element {
	const { cloudId, hasDialogAutoOpened, product, edition } = useEditionAwareness();
	const modalProps = useMemo(() => ({ width: 968, height: 568 }), []);

	const shouldAutoOpen = useMemo(
		() => !hasDialogAutoOpened('feature-onboarding-modal'),
		[hasDialogAutoOpened],
	);
	const markDialogAsSeen = useMarkDialogAsSeen();

	const hasMarked = useRef(false);

	useEffect(() => {
		if (hasMarked.current || !shouldAutoOpen) {
			return;
		}
		markDialogAsSeen('feature-onboarding-modal', product, edition);
		hasMarked.current = true;
	}, [shouldAutoOpen, markDialogAsSeen, product, edition]);

	const [isAutoOpened, setIsAutoOpened] = useState(shouldAutoOpen);

	const entryPointProps = useMemo(
		() => ({
			isAutoOpened,
			onClose() {
				setIsAutoOpened(false);
			},
		}),
		[isAutoOpened],
	);

	const entryPointParams: EntryPointParams = useMemo(
		() => ({ cloudId, hamsProductKey: getHamsKey(product) }),
		[cloudId, product],
	);

	const analyticsAttributes = useMemo(
		() => ({
			isAutoOpened,
		}),
		[isAutoOpened],
	);

	return (
		<AnalyticsContext data={analyticsAttributes}>
			<ModalEntryPoint
				entryPoint={entryPoint}
				modalProps={modalProps}
				dialogConfig={DIALOG_CONFIG}
				entryPointProps={entryPointProps}
				shouldAutoOpen={shouldAutoOpen}
				entryPointParams={entryPointParams}
			/>
		</AnalyticsContext>
	);
}
